import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Math from "@/components/Math";
import { ArrowLeft, ArrowRight, ArrowLeftCircle, BookOpen, Target } from "lucide-react";

export default function LearnLesson() {
  const { lessonId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/learn/lessons/${lessonId}`).then((r) => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
    // Scroll to top on lesson change
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [lessonId]);

  if (loading) return <div className="text-zinc-500 text-sm" data-testid="learn-lesson-loading">Loading lesson…</div>;
  if (!data) return <div className="text-red-300 text-sm">Lesson not found.</div>;

  const { course, unit, lesson, siblings } = data;
  const idx = siblings.findIndex((l) => l.id === lesson.id);
  const prev = idx > 0 ? siblings[idx - 1] : null;
  const next = idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null;

  return (
    <div data-testid="learn-lesson-page" className="max-w-3xl">
      <Link
        to={`/learn/unit/${unit.id}`}
        className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-yellow-300 mb-4"
        data-testid="learn-back-unit"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to {unit.title}
      </Link>

      <div className="mb-2 fade-up">
        <div className="text-[10px] uppercase tracking-[0.2em] text-pink-300 mb-1">
          {course.title} · Unit {unit.number}: {unit.title}
        </div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">
          {lesson.title}
        </h1>
        <div className="text-[11px] text-zinc-500 font-mono mt-1">
          Lesson {idx + 1} of {siblings.length}
        </div>
      </div>

      {/* Lesson body */}
      <article
        className="mt-6 mb-8 rounded-lg border border-white/10 bg-[#152040] p-6 text-zinc-200 leading-relaxed fade-up"
        data-testid="learn-lesson-body"
      >
        <div className="prose prose-invert prose-sm max-w-none text-sm">
          <Math>{lesson.body || "This lesson doesn't have a written explanation yet."}</Math>
        </div>
      </article>

      {/* Prev / next */}
      <div className="flex items-center justify-between gap-3 mb-6">
        {prev ? (
          <Link
            to={`/learn/lesson/${prev.id}`}
            className="fade-up flex items-center gap-2 rounded-lg border border-white/10 bg-[#152040] px-4 py-3 hover:border-yellow-300/40 transition-colors text-left min-w-0"
            data-testid="learn-lesson-prev"
          >
            <ArrowLeftCircle className="w-4 h-4 text-yellow-300 shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Previous</div>
              <div className="text-xs text-zinc-100 truncate">{prev.title}</div>
            </div>
          </Link>
        ) : <div />}
        {next ? (
          <Link
            to={`/learn/lesson/${next.id}`}
            className="fade-up flex items-center gap-2 rounded-lg border border-white/10 bg-[#152040] px-4 py-3 hover:border-yellow-300/40 transition-colors text-right min-w-0 ml-auto"
            data-testid="learn-lesson-next"
          >
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Next</div>
              <div className="text-xs text-zinc-100 truncate">{next.title}</div>
            </div>
            <ArrowRight className="w-4 h-4 text-yellow-300 shrink-0" />
          </Link>
        ) : (
          <Link
            to={`/learn/unit/${unit.id}`}
            className="fade-up flex items-center gap-2 rounded-lg border border-pink-300/30 bg-pink-300/5 px-4 py-3 hover:border-pink-300/60 transition-colors text-right ml-auto"
            data-testid="learn-lesson-finish"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-pink-300">Unit complete</div>
              <div className="text-xs text-zinc-100">Try the practice skills</div>
            </div>
            <Target className="w-4 h-4 text-pink-300" />
          </Link>
        )}
      </div>

      {/* Quick jump */}
      <div className="rounded-lg border border-white/5 bg-white/[0.02] p-4">
        <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
          <BookOpen className="w-3 h-3" /> All lessons in this unit
        </div>
        <ul className="grid sm:grid-cols-2 gap-1.5">
          {siblings.map((s) => (
            <li key={s.id}>
              <Link
                to={`/learn/lesson/${s.id}`}
                className={`block text-xs rounded px-2 py-1.5 hover:bg-white/5 transition-colors ${
                  s.id === lesson.id ? "text-yellow-300 bg-white/5" : "text-zinc-400"
                }`}
              >
                {s.order}. {s.title}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
